import React, { useState } from 'react';
import { fetchUpsBlocklist } from '../services/api';
import { adminWrite } from '../services/api';
import { EditDialog, Field, ListCard, PageTitle, WriteOffNote, useRows, useSave, useWriteLive } from '../components/StaffList';

/**
 * Tools & Lists > Blocked UPS Access Points: stores the checkout pickup picker must not offer
 * (Azure exit WS8). Reads sql01.UPSAccessPointBlocklist. Saving is off until ws8_ups_blocklist_live.
 */

const showDate = (ymd) => { if (!ymd) return ''; const [y, m, d] = ymd.split('-'); return `${m}/${d}/${y}`; };
const dash = (v) => v || '-';

export default function UpsBlocklist() {
  const live = useWriteLive('ws8_ups_blocklist');
  const [search, setSearch] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [edit, setEdit] = useState(null);
  const [store, setStore] = useState(null);
  const [note, setNote] = useState(null);
  const { rows, error, reload } = useRows(() => fetchUpsBlocklist({ search, includeDeleted }), [search, includeDeleted]);
  const { save, saving, error: saveError, setError } = useSave(reload);

  const open = (r) => {
    setError(null);
    setNote(null);
    setStore(r ? { accessPointId: r.accessPointId, name: r.storeName, address: r.address, city: r.city, state: r.state, zip: r.zip } : null);
    setEdit(r ? { id: r.id, orderNumber: r.orderNumber || '', accessPointId: r.accessPointId, reason: r.reason || '' } : { id: 0, orderNumber: '', accessPointId: '', reason: '' });
  };
  const show = (res) => { setStore(res.store || null); setNote(res.message || null); };
  const lookupOrder = async () => {
    if (!edit.orderNumber.trim()) return setNote('Enter an order number first.');
    const res = await adminWrite('ups_ap_lookup_order', { orderNumber: edit.orderNumber }).catch((e) => ({ message: e.message }));
    show(res);
    if (res.store) setEdit((f) => ({ ...f, accessPointId: res.store.accessPointId, orderNumber: res.orderNumber || f.orderNumber }));
    return null;
  };
  const lookupId = async () => {
    if (!edit.accessPointId.trim()) return setNote('Enter an access point id first.');
    show(await adminWrite('ups_ap_lookup', { accessPointId: edit.accessPointId }).catch((e) => ({ message: e.message })));
    return null;
  };
  const typeId = (value) => {
    setEdit({ ...edit, accessPointId: value });
    if (store && String(store.accessPointId || '').toUpperCase() !== value.trim().toUpperCase()) { setStore(null); setNote(null); }
  };
  const submit = async () => {
    const id = edit.accessPointId.trim().toUpperCase();
    if (!id) return setError('Look up the order, or enter the UPS Access Point Id.');
    const s = store && String(store.accessPointId || '').toUpperCase() === id ? store : {};
    if (await save('ups_block_save', { ...edit, accessPointId: id, storeName: s.name, address: s.address, city: s.city, state: s.state, zip: s.zip })) setEdit(null);
    return null;
  };
  const unblock = (r) => {
    // eslint-disable-next-line no-alert
    if (window.confirm(`Unblock ${r.storeName || 'this store'} (${r.accessPointId}) in ${r.city || ''} ${r.state || ''}? Customers will be able to pick it at checkout again within a minute.`)) {
      save('ups_block_delete', { id: r.id });
    }
  };

  return (
    <div>
      <PageTitle>Blocked UPS Access Points</PageTitle>
      <div className="staff-hint">Stores listed here are never offered in the checkout UPS pickup picker. Use it for an access point that charged one of our customers a fee to pick up their box.</div>
      <WriteOffNote live={live} what="Blocking, editing and unblocking" />
      <div className="port-filters">
        <input type="search" className="form-input" style={{ width: 'auto' }} placeholder="Search" aria-label="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
        <label className="staff-check"><input type="checkbox" checked={includeDeleted} onChange={(e) => setIncludeDeleted(e.target.checked)} /> Show unblocked stores</label>
        <button type="button" className="btn btn-primary" disabled={!live} onClick={() => open(null)}>Block a UPS Access Point</button>
      </div>
      <ListCard title="Blocked stores" rows={rows} error={error} empty="No blocked stores.">
        <table className="trans-table">
          <thead><tr><th>Store</th><th>Access Point</th><th>Order</th><th>Reason</th><th>Blocked By</th><th>Blocked</th><th aria-label="Actions" /></tr></thead>
          <tbody>
            {(rows || []).map((r) => (
              <tr key={r.id} className={r.deletedAt ? 'staff-row-off' : ''}>
                <td>{dash(r.storeName)}<br />{r.address}, {r.city} {r.state} {r.zip}</td>
                <td>{r.accessPointId}</td>
                <td>{dash(r.orderNumber)}</td>
                <td>{dash(r.reason)}</td>
                <td>{dash(r.createdBy)}</td>
                <td>{showDate(r.createdAt)}</td>
                <td className="staff-actions">
                  {r.deletedAt ? `unblocked ${showDate(r.deletedAt)}` : (
                    <>
                      <button type="button" className="btn btn-ghost" disabled={!live || saving} onClick={() => open(r)}>Edit</button>
                      <button type="button" className="btn btn-ghost" disabled={!live || saving} onClick={() => unblock(r)}>Unblock</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ListCard>
      {!edit && saveError && <div className="staff-msg staff-msg-error">{saveError}</div>}
      {edit && (
        <EditDialog title={edit.id > 0 ? 'Edit Blocked Store' : 'Block a UPS Access Point'} live={live} saving={saving} error={saveError} onClose={() => setEdit(null)} onSave={submit}>
          <Field label="Order the customer reported">
            <div className="staff-inline">
              <input className="form-input" maxLength={50} placeholder="e.g. 2026903" value={edit.orderNumber} onChange={(e) => setEdit({ ...edit, orderNumber: e.target.value })} />
              <button type="button" className="btn btn-outline" onClick={lookupOrder}>Look up</button>
            </div>
          </Field>
          <Field label="UPS Access Point Id">
            <div className="staff-inline">
              <input className="form-input" maxLength={20} placeholder="e.g. U43737015" value={edit.accessPointId} onChange={(e) => typeId(e.target.value)} />
              <button type="button" className="btn btn-outline" onClick={lookupId}>Look up</button>
            </div>
          </Field>
          {store && <div className="staff-store"><strong>{store.name}</strong><br />{store.address}<br />{store.city} {store.state} {store.zip}</div>}
          {note && <div className="staff-hint">{note}</div>}
          <Field label="Reason (optional)"><textarea className="form-textarea" maxLength={500} placeholder="e.g. Charged the customer $10 to pick up their box." value={edit.reason} onChange={(e) => setEdit({ ...edit, reason: e.target.value })} /></Field>
        </EditDialog>
      )}
    </div>
  );
}
